<script lang="ts">
	import type { RouteWithGeometry } from '$lib/components//map/types';
	import Button from '$lib/components/atoms/button/Button.svelte';
	import Link from '$lib/components/atoms/link/Link.svelte';
	import { distanceUnitLabel } from '$lib/components/constants';
	import Section from '$lib/components/molecules/section/Section.svelte';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import { formatPrice } from '$lib/utils/formatPrice';
	import * as m from '$paraglide/messages';
	import { languageTag } from '$paraglide/runtime';
	import { createEventDispatcher, onMount } from 'svelte';
	import { getExpenseNotePrice } from '../getExpenseNotePrice';
	import { getExpenseNoteContext } from './expense-note-context';
	import ExpenseNoteLine from './ExpenseNoteLine.svelte';

	export let route: RouteWithGeometry;
	export let from: string;
	export let to: string;
	export let locale: string;

	const dispatch = createEventDispatcher<{
		editExpenseNote: void;
	}>();

	const generatedDate = new Intl.DateTimeFormat(locale).format(new Date());

	const expenseNote = getExpenseNoteContext();
	const {
		mileageAllowance,
		additionalInfo,
		date,
		identity,
		includeTolls,
		includeVignettes,
		computeMethod,
		includeEnergy,
		energyCostPerUnit,
	} = $expenseNote;

	onMount(() => {
		$expenseNote.energyCostPerUnit = route.energyCost?.amount.toString() ?? '0';
	});

	function formatAmount(amount: number) {
		return formatPrice({
			amount,
			// TODO: change this when trip option currency select feature is done
			currency: 'EUR',
			language: languageTag(),
			digits: 2,
		});
	}

	const KILOMETER_TO_METERS = 1000;
	const MILE_TO_FEET = 5280;
	$: mileageAllowanceDistance
		= route.routeDistance.unit === 'METER'
			? route.routeDistance.value / KILOMETER_TO_METERS
			: route.routeDistance.unit === 'FOOT'
			? route.routeDistance.value / MILE_TO_FEET
			: route.routeDistance.value;
	$: mileageAllowanceAmount
		= mileageAllowanceDistance && Number.parseFloat(mileageAllowance) * mileageAllowanceDistance;
	$: energyAmount
		= Number.parseFloat(energyCostPerUnit)
		* ((route.isEv ? route.electricityConsumption : route.fuelConsumption) ?? 0);

	$: totalPriceAmount = getExpenseNotePrice({
		mileageAllowanceAmount,
		energyAmount,
		includeEnergy,
		includeTolls,
		includeVignettes,
		computeMethod,
		routeCosts: route.costs,
	});

	$: mileageAllowancePrice = formatAmount(mileageAllowanceAmount);
	$: energyPrice = formatAmount(energyAmount);
	$: tollsPrice = formatAmount(route.costs.tolls.amount);
	$: vignettesPrice = formatAmount(route.costs.vignette.amount);
	$: totalPrice = formatAmount(totalPriceAmount);
	$: printSearchParams = new URLSearchParams({
		mileageAllowance: mileageAllowancePrice,
		energy: energyPrice,
		tolls: tollsPrice,
		vignettes: vignettesPrice,
		totalPrice,
		additionalInfo,
		date,
		identity,
		includeTolls: includeTolls.toString(),
		includeVignettes: includeVignettes.toString(),
		includeEnergy: includeEnergy.toString(),
		computeMethod,
		from,
		to,
		distance: `${route.routeDistance.value} ${distanceUnitLabel[route.routeDistance.unit as keyof typeof distanceUnitLabel]}`,
		isEv: (route.isEv ?? false).toString(),
		locale, // Needed on staging preview and local as long as we can't determine the language from the domain name
	});

	$: printUrl = `/print?${printSearchParams.toString()}`;
</script>

<div class="grid gap-8" data-testid="expense-note">
	<div class="flex flex-col md:flex-row-reverse gap-8 md:justify-between md:items-center">
		<div class="flex gap-2">
			<Button
				icon="edit"
				text={m.action__edit()}
				variant="outline"
				size="md"
				onclick={() => dispatch('editExpenseNote')}
			/>
			<Link
				button="filled"
				icon="print"
				label={m.action__print()}
				size="md"
				href={printUrl}
				target="_blank"
				onclick={() =>
					track({
						action: 'itinerary_print',
						payload: {
							action: 'print',
						},
					})}
			/>
		</div>
		<div>
			<div class="font-arvo text-6">{m.route_details__expense_note()}</div>
			<div class="text-sm">
				{m.route_details__expense_note_section__generated_on({ date: generatedDate })}
			</div>
		</div>
	</div>

	{#if additionalInfo}
		<Section title={m.route_details__expense_note_section__notes()}>
			<div>{additionalInfo}</div>
		</Section>
	{/if}

	<Section title={m.route_details__expense_note_section__travel()}>
		<div class="section text-md">
			<ExpenseNoteLine
				title={m.route_details__expense_note_section__date()}
				value={date}
				truncateValue
			/>
			<ExpenseNoteLine
				title={m.route_details__expense_note_section__identity()}
				value={identity}
				truncateValue
			/>
			<ExpenseNoteLine
				title={m.route_details__expense_note_section__departure()}
				value={from}
				truncateValue
			/>
			<ExpenseNoteLine
				title={m.route_details__expense_note_section__arrival()}
				value={to}
				truncateValue
			/>
			<ExpenseNoteLine
				title={m.route_details__expense_note_section__distance()}
				value={`${route.routeDistance.value} ${distanceUnitLabel[route.routeDistance.unit as keyof typeof distanceUnitLabel]}`}
			/>
		</div>
	</Section>

	<Section title={m.route_details__expense_note_section__costs()}>
		<div class="section text-md">
			{#if computeMethod === 'allowance'}
				<ExpenseNoteLine
					title={m.route_details__expense_note_section__allowance()}
					value={mileageAllowancePrice}
					truncateValue
				/>
			{:else if includeEnergy}
				<ExpenseNoteLine
					title={route.isEv
						? m.route_details__expense_note_section__electricity()
						: m.route_details__expense_note_section__fuel()}
					value={energyPrice}
					truncateValue
				/>
			{/if}
			{#if includeTolls}
				<ExpenseNoteLine
					title={m.route_details__expense_note_section__tolls()}
					value={tollsPrice}
					truncateValue
				/>
			{/if}
			{#if includeVignettes}
				<ExpenseNoteLine
					title={m.route_details__expense_note_section__vignette()}
					value={vignettesPrice}
					truncateValue
				/>
			{/if}
		</div>
		<div class="flex justify-between gap-8 border-t-solid border-t-1 border-neutral-soft pt-4">
			<ExpenseNoteLine
				title={m.route_details__expense_note_section__total()}
				value={totalPrice}
				truncateValue
			/>
		</div>
	</Section>
</div>

<style>
	.section {
		display: grid;
		grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
		gap: 8px;
		row-gap: 8px;
		column-gap: 16px;
	}
</style>
